import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BookOpen, Award, TrendingUp, ClipboardCheck, ArrowRight, Sparkles, Layers, Video, Target } from "lucide-react";

export default function DashboardPage() {
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [m, p] = await Promise.all([api.get("/modules"), api.get("/progress")]);
        setModules(m.data);
        setProgress(p.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <AppLayout>
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-7xl mx-auto" data-testid="dashboard-page">
        {/* Header */}
        <div className="mb-10">
          <div className="overline text-[var(--dw-gold-soft)] mb-3">Welcome back</div>
          <h1 className="font-serif text-4xl md:text-5xl text-[var(--dw-navy)] mb-2 tracking-tight" data-testid="dashboard-greeting">
            {greeting()}, {user?.name?.split(" ")[0] || "Candidate"}.
          </h1>
          <p className="text-neutral-600">Continue where you left off, or dive into today's practice.</p>
        </div>

        {/* Stat grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard icon={TrendingUp} label="Overall Progress" value={`${progress?.overall_pct || 0}%`} sub={`${progress?.total_completed || 0} of ${progress?.total_lessons || 0} lessons`} testid="stat-progress" />
          <StatCard icon={ClipboardCheck} label="Quiz Attempts" value={progress?.total_attempts || 0} sub={`Avg. ${progress?.avg_score || 0}%`} testid="stat-attempts" />
          <StatCard icon={Award} label="Best Streak" value={`${(progress?.recent_attempts?.filter(a => a.score_pct >= 70).length) || 0}`} sub="Passing scores" testid="stat-streak" />
          <StatCard icon={BookOpen} label="Modules" value={`${Object.values(progress?.per_module || {}).filter(m => m.pct === 100).length}/5`} sub="Fully completed" testid="stat-modules" />
        </div>

        {/* Continue + Curriculum */}
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="overline mb-1">Your curriculum</div>
                <h2 className="font-serif text-2xl text-[var(--dw-navy)]">Continue studying</h2>
              </div>
              <Link to="/modules" className="text-sm text-neutral-500 hover:text-[var(--dw-navy)] flex items-center gap-1" data-testid="view-all-modules">
                All modules <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
              </Link>
            </div>

            <div className="space-y-3">
              {loading && [1, 2, 3].map((i) => <div key={i} className="dw-card h-24 animate-pulse" />)}
              {!loading && modules.map((m) => {
                const p = progress?.per_module?.[m.id] || { pct: 0, completed: 0, total: m.lesson_count };
                return (
                  <Link
                    to={`/modules/${m.id}`}
                    key={m.id}
                    data-testid={`dashboard-module-${m.id}`}
                    className="dw-card block p-5 group hover:border-[var(--dw-gold)]"
                  >
                    <div className="flex items-center gap-5">
                      <div className="font-serif text-3xl text-[var(--dw-gold-soft)] w-12 shrink-0">0{m.id}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <div className="font-serif text-lg text-[var(--dw-navy)] truncate">{m.title}</div>
                          <span className="text-[10px] uppercase tracking-widest text-neutral-500 shrink-0">{m.weight}% weight</span>
                        </div>
                        <div className="text-xs text-neutral-500 mb-2 truncate">{m.subtitle}</div>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-1 bg-[var(--dw-navy)]/10 rounded-sm overflow-hidden">
                            <div className="dw-progress-bar h-full" style={{ width: `${p.pct}%` }} />
                          </div>
                          <span className="text-xs font-mono-plex text-neutral-600 shrink-0">{p.completed}/{p.total}</span>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-neutral-400 group-hover:text-[var(--dw-navy)] group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Side */}
          <div className="space-y-5">
            <Link to="/practice-exam" data-testid="practice-exam-card" className="dw-card block p-6 relative overflow-hidden group border-[var(--dw-navy)]/12 hover:border-[var(--dw-gold)]">
              <div className="dw-grid-lines absolute inset-0 opacity-40" />
              <div className="relative">
                <ClipboardCheck className="w-6 h-6 text-[var(--dw-gold-soft)] mb-3" strokeWidth={1.5} />
                <div className="overline mb-1">Full Mock Exam</div>
                <div className="font-serif text-xl text-[var(--dw-navy)] mb-2">Sit the practice exam</div>
                <div className="text-xs text-neutral-600 mb-4 leading-relaxed">
                  Timed or untimed — 100 questions weighted to the NMLS blueprint.
                </div>
                <div className="text-xs font-medium text-[var(--dw-navy)] flex items-center gap-1 group-hover:gap-2 transition-all">
                  Begin <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </div>
              </div>
            </Link>

            <div className="grid grid-cols-2 gap-3">
              <Link to="/flashcards" data-testid="flashcards-card" className="dw-card block p-4 group hover:border-[var(--dw-gold)]">
                <Layers className="w-5 h-5 text-[var(--dw-gold-soft)] mb-2" strokeWidth={1.5} />
                <div className="overline mb-1">180 cards</div>
                <div className="font-serif text-base text-[var(--dw-navy)] leading-tight">Flashcards</div>
              </Link>
              <Link to="/videos" data-testid="videos-card" className="dw-card block p-4 group hover:border-[var(--dw-gold)]">
                <Video className="w-5 h-5 text-[var(--dw-gold-soft)] mb-2" strokeWidth={1.5} />
                <div className="overline mb-1">28 lectures</div>
                <div className="font-serif text-base text-[var(--dw-navy)] leading-tight">Video Library</div>
              </Link>
              <Link to="/missed-drill" data-testid="missed-drill-card" className="dw-card block p-4 group hover:border-[var(--dw-gold)]">
                <Target className="w-5 h-5 text-[var(--dw-gold-soft)] mb-2" strokeWidth={1.5} />
                <div className="overline mb-1">Personal drill</div>
                <div className="font-serif text-base text-[var(--dw-navy)] leading-tight">Missed Q's</div>
              </Link>
              <Link to="/tutor" data-testid="tutor-card" className="dw-card block p-4 group hover:border-[var(--dw-gold)]">
                <Sparkles className="w-5 h-5 text-[var(--dw-gold-soft)] mb-2" strokeWidth={1.5} />
                <div className="overline mb-1">Claude 4.5</div>
                <div className="font-serif text-base text-[var(--dw-navy)] leading-tight">AI Tutor</div>
              </Link>
            </div>

            {progress?.recent_attempts?.length > 0 && (
              <div className="dw-card p-5">
                <div className="overline mb-3">Recent attempts</div>
                <div className="space-y-2">
                  {progress.recent_attempts.slice(0, 5).map((a, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-neutral-600 truncate">
                        {a.mode === "practice_exam" ? "Practice Exam" : `Module ${a.module_id}`}
                      </span>
                      <span className={`font-mono-plex text-xs px-2 py-0.5 rounded-sm ${
                        a.score_pct >= 75 ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-red-50 text-red-800 border border-red-200"
                      }`}>{a.score_pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="dw-card p-4 md:p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="overline">{label}</div>
        <Icon className="w-4 h-4 text-[var(--dw-gold-soft)]" strokeWidth={1.5} />
      </div>
      <div className="font-serif text-3xl text-[var(--dw-navy)] leading-none mb-1">{value}</div>
      <div className="text-xs text-neutral-500">{sub}</div>
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}
